import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { useColumnEntityReorderStore } from '../providers';
import { usePendoTrackingForDragDrop } from './hooks';

export const ColumnEntityReorderActions = () => {
  const { t } = useTranslation('schedule');
  const { columnReorderHandlers, isReordering, resetColumnReorderHandlers } = useColumnEntityReorderStore([
    'columnReorderHandlers',
    'isReordering',
    'resetColumnReorderHandlers',
  ]);

  const [columnEntityOrder, setColumnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');

  const trackConfirmEvent = usePendoTrackingForDragDrop({
    eventName: 'schedule_column_order_saved',
  });

  const trackCancelEvent = usePendoTrackingForDragDrop({
    eventName: 'schedule_column_reorder_canceled',
  });

  const onConfirm = () => {
    if (columnReorderHandlers) {
      const newCompleteOrderMap = Object.values(columnReorderHandlers).reduce(
        (finalOrderMap, handler) => {
          if (handler && handler.confirm && typeof handler.confirm === 'function') {
            const [calendarEntityType, locationId, newOrderIdMap] = handler.confirm();
            finalOrderMap[calendarEntityType] = {
              ...(finalOrderMap[calendarEntityType] ?? {}),
              [locationId]: newOrderIdMap,
            };
          }
          return finalOrderMap;
        },
        { ...columnEntityOrder }
      );
      trackConfirmEvent();
      setColumnEntityOrder(newCompleteOrderMap);
    }
  };

  const onCancel = () => {
    trackCancelEvent();
    if (columnReorderHandlers) {
      Object.values(columnReorderHandlers).forEach(
        (handler) => handler && handler.cancel && typeof handler.cancel === 'function' && handler.cancel()
      );
    } else {
      resetColumnReorderHandlers();
    }
  };

  return (
    <div css={styles.container}>
      <Button onClick={onCancel} variant='secondary' disabled={isReordering}>
        {t('Cancel')}
      </Button>
      <Button onClick={onConfirm} disabled={isReordering} loading={isReordering}>
        {t('Save Layout')}
      </Button>
    </div>
  );
};

const styles = {
  container: css`
    padding: ${theme.spacing(1)};
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: flex-end;
    border-bottom: 1px solid ${theme.colors.neutral20};
  `,
};
